/**
 * Cookie-based auth module for the new OTP auth path (?v2 / authProvider=cognito).
 *
 * The session is maintained via httpOnly cookies on the API domain, refreshed
 * through /auth/refresh. On cold page load, initAuth probes /auth/refresh to
 * detect whether existing cookies are still valid and repopulate the in-memory
 * user payload.
 *
 * authFetch-equivalent behaviour is implemented in services.js via the
 * callWithCookieAuth helper, which sends the cookie and calls refresh() on
 * 401 before retrying once. Requests carry no Authorization header.
 *
 * The legacy JWT path (JWTContext / localStorage) is NOT touched by this module.
 */

const AUTH_BASE = process.env.REACT_APP_USER_SERVICE_URL;

const COOKIE_AUTHED_HINT_KEY = "atomic.cookieAuthed";

// "We think we're authed" hint. The actual cookies live on the API domain and
// are not readable from JS. This hint lets the auth gate render optimistically
// and avoid an extra /auth/refresh round-trip when we just logged in.
let cookieAuthedHint =
  typeof window !== "undefined" &&
  sessionStorage.getItem(COOKIE_AUTHED_HINT_KEY) === "1";

function setCookieAuthedHint(value) {
  cookieAuthedHint = value;
  if (typeof window === "undefined") return;
  if (value) sessionStorage.setItem(COOKIE_AUTHED_HINT_KEY, "1");
  else sessionStorage.removeItem(COOKIE_AUTHED_HINT_KEY);
}

// User payload. Lives in module memory only; not persisted. On a cold page
// load, initAuth's /auth/refresh call repopulates it from the cookie.
let _user = null;

export function getUser() {
  return _user;
}

export function isAuthenticated() {
  return cookieAuthedHint;
}

/* ----------------------- auth-changed event bus -------------------------- */

const AUTH_CHANGED_EVENT = "atomic:auth-changed";

function notifyAuthChanged() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
}

export function onAuthChanged(handler) {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(AUTH_CHANGED_EVENT, handler);
  return () => window.removeEventListener(AUTH_CHANGED_EVENT, handler);
}

/* ----------------------- response parsing -------------------------------- */

// The backend reports failures as { error: "<code>" }. Returns "" when the
// body is missing or not JSON.
async function readErrorCode(res) {
  try {
    return (await res.clone().json()).error ?? "";
  } catch {
    return "";
  }
}

const NETWORK_ERROR =
  "We could not reach the server. Check your connection and try again.";

// fetch() rejects with a bare TypeError when the request never completes: a
// blocked CORS preflight, a dropped connection, DNS failure. Its message is
// "Failed to fetch", which tells someone signing in nothing they can act on,
// so every caller that surfaces errors to the form goes through here.
async function postJson(path, body) {
  try {
    return await fetch(`${AUTH_BASE}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new Error(NETWORK_ERROR);
  }
}

// Error codes from POST /auth/otp/start. `otp_unavailable` means the account
// exists but Cognito has no email-OTP challenge for it, which is a
// provisioning gap rather than anything the person can fix themselves.
const START_ERRORS = {
  user_not_found:
    "We couldn't find an account for that email. Check the spelling, or ask " +
    "your account manager to invite you.",
  otp_unavailable:
    "That account is not set up for email sign-in codes yet. Ask your account " +
    "manager to finish setting it up.",
  otp_start_failed: "We could not send a code just now. Try again in a moment.",
};

// Error codes from POST /auth/otp/verify.
const VERIFY_ERRORS = {
  invalid_code: "That code is not right. Check it and try again.",
  session_expired: "That code has expired. Ask for a new one.",
  auth_error: "Something went wrong signing you in. Try again in a moment.",
};

// Parse the user payload returned by /auth/otp/verify and /auth/refresh.
// Returns null on any unexpected shape.
async function readUserResponse(res) {
  try {
    const body = await res.clone().json();
    const u = body.user;
    if (!u || typeof u !== "object") return null;
    const stores = Array.isArray(u.stores)
      ? u.stores.map((s) => ({
          code: String(s.code ?? ""),
          display_name: String(s.display_name ?? s.code ?? ""),
          role: String(s.role ?? ""),
          applications: Array.isArray(s.applications) ? s.applications : [],
          marketplaces: Array.isArray(s.marketplaces)
            ? s.marketplaces.map(String)
            : [],
        }))
      : [];
    const user = {
      email: String(u.email ?? ""),
      first_name: u.first_name ? String(u.first_name) : undefined,
      last_name: u.last_name ? String(u.last_name) : undefined,
      is_superadmin: Boolean(u.is_superadmin),
      is_atomic_employee: Boolean(u.is_atomic_employee),
      stores,
    };
    return user;
  } catch {
    return null;
  }
}

/* ----------------------- silent refresh ---------------------------------- */

// Deduplicates concurrent refresh calls: all callers share the same in-flight
// promise, so a burst of 401s triggers exactly one /auth/refresh request.
let refreshInFlight = null;

export async function refresh() {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${AUTH_BASE}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      const ok = res.ok;
      const prev = cookieAuthedHint;
      setCookieAuthedHint(ok);
      const wasUserNull = _user === null;
      if (ok) {
        const parsed = await readUserResponse(res);
        if (parsed) _user = parsed;
      } else {
        _user = null;
      }
      if (ok !== prev || (wasUserNull && _user !== null)) notifyAuthChanged();
      return ok;
    } catch {
      const prev = cookieAuthedHint;
      setCookieAuthedHint(false);
      _user = null;
      if (prev) notifyAuthChanged();
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/* ----------------------- public API -------------------------------------- */

/**
 * Probe /auth/refresh on cold app load to detect whether existing cookies are
 * still valid. Skipped when we already have both a hint and a user in memory
 * (e.g. same-tab sign-in, not a cold load).
 */
export async function initAuth() {
  if (!cookieAuthedHint || _user === null) {
    await refresh();
  }
}

/**
 * Step 1 of OTP sign-in: request a code to be sent to the user's email.
 * Returns { session, destination } where session is an opaque token needed
 * for verifyOtp and destination is a masked address for display.
 */
export async function startOtp(email) {
  const res = await postJson("/auth/otp/start", { email });
  if (!res.ok) {
    const errorCode = await readErrorCode(res);
    throw new Error(
      START_ERRORS[errorCode] ?? "We could not send a code. Try again."
    );
  }
  const body = await res.json().catch(() => ({}));
  if (!body.session) {
    throw new Error("We could not start sign-in. Try again.");
  }
  return { session: body.session, destination: body.destination ?? "" };
}

/**
 * Step 2 of OTP sign-in: verify the code the user received.
 * On success the backend sets the httpOnly session cookies and returns the
 * user payload.
 */
export async function verifyOtp(email, code, session) {
  const res = await postJson("/auth/otp/verify", { email, code, session });
  if (!res.ok) {
    const errorCode = await readErrorCode(res);
    throw new Error(
      VERIFY_ERRORS[errorCode] ?? "We could not verify that code. Try again."
    );
  }
  const parsed = await readUserResponse(res);
  if (parsed) _user = parsed;
  setCookieAuthedHint(true);
  notifyAuthChanged();
}

/**
 * Revoke the session server-side and clear all local auth state.
 */
export async function logout() {
  try {
    await fetch(`${AUTH_BASE}/auth/logout`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
  } catch {
    // Server-side logout failing is non-fatal; local state still cleared.
  }
  _user = null;
  setCookieAuthedHint(false);
  notifyAuthChanged();
}
